import { ContactType } from "components/shareData/shareDialog";
import { RoleTypes, UserOrg, UserProfile, OrgToOrgSubscription } from "types/auth";
import { EstateProps, MapProps, ProjectProps } from "types/estatesAndMap";
import { sortAccordingToOrder } from "utils/sorting";

const ROLE_HIRARCHY = ["viewer", "commentator", "editor", "admin"];

export interface GetDataOptions {
  user?: UserProfile | null;
  userOrg?: UserOrg | null;
  estates: { [key: string]: EstateProps };
  maps?: { [key: string]: MapProps };
  teamMaps?: { [key: string]: { [key: string]: MapProps } };
  projects?: { [key: string]: ProjectProps };
  teamProjects?: { [key: string]: { [key: string]: ProjectProps } };
  orgEstates?: { [key: string]: EstateProps };
  orgMaps?: { [key: string]: MapProps };
  orgProjects?: { [key: string]: ProjectProps };
  orgToOrgConsumerSubscriptions?: OrgToOrgSubscription[] | null;
  selectedEstateId?: string | null;
  selectedPerson?: ContactType | null;
  overviewText?: string;
  orgName?: string;
  selectAllText?: string;
  withArchivedData?: boolean;
  withOrgAndEstateEmailMatch?: boolean;
  withOrgToOrgConsumerSubscriptionMatchMap?: boolean;
  withOrgToOrgConsumerSubscriptionMatchProject?: boolean;
  withUserEstateRole?: boolean;
  withShowInventories?: boolean;
  withSelectAllOption?: boolean;
  allRoleOptions: { id: RoleTypes; status: string; label: string }[];
  mapKeysNotAllowed?: string[];
  estateKeysNotAllowed?: string[];
  withoutBuyerRole?: boolean;
  externalText?: string;
}
const getDataOptions = ({
  user,
  userOrg,
  estates,
  orgEstates,
  maps,
  teamMaps,
  orgMaps,
  projects,
  teamProjects,
  orgProjects,
  orgToOrgConsumerSubscriptions,
  selectedPerson,
  selectedEstateId,
  withArchivedData,
  withOrgAndEstateEmailMatch,
  withOrgToOrgConsumerSubscriptionMatchMap,
  withOrgToOrgConsumerSubscriptionMatchProject,
  withUserEstateRole,
  withShowInventories,
  withSelectAllOption,
  overviewText,
  selectAllText = "Select All",
  orgName,
  allRoleOptions,
  mapKeysNotAllowed,
  estateKeysNotAllowed,
  withoutBuyerRole,
  externalText,
}: GetDataOptions) => {
  // Check if user is logged in and if userOrg is available, etc.
  if (!user) return { uniqueEstates: [], list: [] };
  if (withOrgAndEstateEmailMatch && (!userOrg || !estates)) return { uniqueEstates: [], list: [] };
  // Form ignore list
  let mapsIgnore: string[] = [];
  let projectsIgnore: string[] = [];
  if (selectedPerson) {
    if (selectedPerson.ignoreMaps) mapsIgnore = selectedPerson.ignoreMaps.map((el: any) => el.id);
    if (selectedPerson.ignoreProjects)
      projectsIgnore = selectedPerson.ignoreProjects.map((el: any) => el.id);
  }
  let list: any[] = [];
  let userEstateRole: { [key: string]: RoleTypes } = {};
  let tempMapList: any[] = [];
  let tempProjList: any[] = [];
  // Create org maps list
  if (orgMaps && Object.keys(orgMaps).length > 0) {
    Object.values(orgMaps).forEach((el, idx) => {
      if (!mapsIgnore.includes(el.id)) {
        mapsIgnore.push(el.id);
        // Check for person role on data element
        let roleOptions = allRoleOptions;
        let role = "admin";
        if (selectedPerson) {
          // Find role of selected person
          role = getCurrentDataRole({ userId: selectedPerson.id, el });
        }
        if (user.orgRole !== "admin") {
          roleOptions = getRoleOptions({ allRoleOptions, el, userId: user.id });
        }
        const estateId = el.estateId;
        const estateName = el.estateName;
        const limitDisabled =
          !!mapKeysNotAllowed?.includes(el.id) ||
          !!estateKeysNotAllowed?.includes(estateId);
        if (selectedEstateId && selectedEstateId !== estateId) return;
        tempMapList.push({
          estateId: estateId,
          mapId: el.id,
          estateName: estateName,
          mapName: el.name,
          tagField: el.name,
          dataElType: "map",
          beingUpdated: el.beingUpdated,
          label: estateName + ":" + el.name + ":map-" + idx,
          roleOptions: roleOptions,
          role: role,
          disabled: limitDisabled,
        });
      }
    });
  }
  // Create user maps list
  if (maps && Object.keys(maps).length > 0) {
    Object.values(maps).forEach((el, idx) => {
      // Check if only maps for estates that match the user's email should be returned
      const estateId = el.estateId;
      const estateName = el.estateName;
      const currentRole = getCurrentDataRole({ userId: user.id, el });
      if (
        !userEstateRole[estateId] ||
        ROLE_HIRARCHY.indexOf(currentRole) < ROLE_HIRARCHY.indexOf(userEstateRole[estateId])
      ) {
        userEstateRole[estateId] = currentRole;
      }
      if (withOrgAndEstateEmailMatch && currentRole !== "admin") {
        const match = checkEstateAndOrgEmailMatch({ userOrg, estates, el });
        if (!match) return;
      }
      if (withOrgToOrgConsumerSubscriptionMatchMap && currentRole !== "admin") {
        const match = checkOrgToOrgConsumerSubscriptionMatch({
          orgToOrgConsumerSubscriptions,
          el,
        });
        if (!match) return;
      }
      const limitDisabled =
        !!mapKeysNotAllowed?.includes(el.id) ||
        !!estateKeysNotAllowed?.includes(estateId);
      // Check for disable of estate
      let disableEstate = selectedPerson ? true : false;

      // Proceed with adding the map to the list
      if (!mapsIgnore.includes(el.id)) {
        mapsIgnore.push(el.id);
        const roleOptions = getRoleOptions({ allRoleOptions, el, userId: user.id });
        // Check if only maps for specific estate should be returned
        if (selectedEstateId && selectedEstateId !== estateId) return;
        // else return maps
        tempMapList.push({
          estateId: estateId,
          mapId: el.id,
          estateName: estateName,
          mapName: el.name,
          tagField: el.name,
          dataElType: "map",
          beingUpdated: el.beingUpdated,
          label: estateName + ":" + el.name + ":map-" + idx,
          roleOptions: roleOptions,
          role: currentRole,
          disableEstate: disableEstate,
          disabled: limitDisabled,
        });
      }
    });
  }

  // Insert team maps
  if (teamMaps && Object.keys(teamMaps).length > 0) {
    Object.keys(teamMaps).forEach((teamMemberId) => {
      if (selectedPerson && selectedPerson.id === teamMemberId) return;
      const teamMemberMaps = teamMaps[teamMemberId];
      Object.values(teamMemberMaps).forEach((el, idx) => {
        if (!mapsIgnore.includes(el.id)) {
          mapsIgnore.push(el.id);
          // Get person role based on team member and all role options
          const currentRole = getCurrentDataRole({ userId: teamMemberId, el });
          const roleOptions = getRoleOptions({ allRoleOptions, el, userId: teamMemberId });
          const roleData = getRoleData({ el, userId: teamMemberId });
          const estateId = el.estateId;
          const estateName = el.estateName;
          const limitDisabled =
            !!mapKeysNotAllowed?.includes(el.id) ||
            !!estateKeysNotAllowed?.includes(estateId);
          if (selectedEstateId && selectedEstateId !== estateId) return;
          tempMapList.push({
            estateId: estateId,
            mapId: el.id,
            estateName: estateName,
            mapName: el.name,
            tagField: el.name,
            dataElType: "map",
            beingUpdated: el.beingUpdated,
            label: estateName + ":" + el.name + ":map-" + idx,
            disableEstate: true,
            roleOptions: roleOptions,
            role: currentRole,
            categories: roleData && roleData.categories ? roleData.categories : undefined,
            expireDate: roleData && roleData.expireDate ? roleData.expireDate : undefined,
            inviterId: teamMemberId,
            disabled: limitDisabled,
          });
        }
      });
    });
  }

  // Sort the list according to label
  if (tempMapList.length > 0) {
    tempMapList = tempMapList.sort((a, b) => {
      const valueA = typeof a.label === "string" ? a.label : "";
      const valueB = typeof b.label === "string" ? b.label : "";
      return valueA.localeCompare(valueB);
    });
  }

  // Create org projects list
  if (orgProjects && Object.keys(orgProjects).length > 0) {
    Object.values(orgProjects).forEach((el, idx) => {
      // Check if archived projects should be included
      if (!withArchivedData && el.projectStatus === "archived") return;
      if (!projectsIgnore.includes(el.id)) {
        projectsIgnore.push(el.id);
        // Check for org admin
        let roleOptions = allRoleOptions;
        let role = "admin";
        if (selectedPerson) {
          // Find role of selected person
          role = getCurrentDataRole({ userId: selectedPerson.id, el });
        }
        if (user.orgRole !== "admin") {
          roleOptions = getRoleOptions({ allRoleOptions, el, userId: user.id });
        }
        let estateId = el.estateId;
        let estateName = el.estateName;
        const limitDisabled = !!estateKeysNotAllowed?.includes(estateId);
        if (selectedEstateId && selectedEstateId !== estateId) return;
        let dataElType = "project";
        if (el.projectStatus === "archived") dataElType = "archivedProject";
        
        tempProjList.push({
          estateId: estateId,
          projectId: el.id,
          estateName: estateName,
          projectName: el.name,
          mapName: el.name,
          tagField: el.name,
          dataElType,
          label: estateName + ":" + el.name + ":project-" + idx,
          role: role,
          roleOptions: roleOptions,
          disabled: limitDisabled,
        });
      }
    });
  }
  // Create projects list
  if (projects && Object.keys(projects).length > 0) {
    Object.values(projects).forEach((el, idx) => {
      // Check if only maps for estates that match the user's email should be returned
      if (withOrgAndEstateEmailMatch && !checkEstateAndOrgEmailMatch({ userOrg, estates, el })) {
        return;
      }
      if (
        withOrgToOrgConsumerSubscriptionMatchProject &&
        !checkOrgToOrgConsumerSubscriptionMatch({ orgToOrgConsumerSubscriptions, el })
      ) {
        return;
      }

      const limitDisabled = !!estateKeysNotAllowed?.includes(el.estateId);
      // Check if archived projects should be included
      if (!withArchivedData && el.projectStatus === "archived") return;
      // Proceed with adding the project to the list
      if (!projectsIgnore.includes(el.id)) {
        projectsIgnore.push(el.id);
        let estateId = el.estateId;
        let estateName = el.estateName;
        // Check if user has buyer role only and withoutBuyerRole is set
        if (withoutBuyerRole) {
          const currentRole = getCurrentDataRole({ userId: user.id, el });
          if (currentRole === "buyer") return; // Skip projects where user is only a buyer
        }
        const roleOptions = getRoleOptions({ allRoleOptions, el, userId: user.id });
        // Check if only projects for specific estate should be returned
        if (selectedEstateId && selectedEstateId !== estateId) return;
        let dataElType = "project";
        if (el.projectStatus === "archived") dataElType = "archivedProject";
        tempProjList.push({
          estateId: estateId,
          projectId: el.id,
          estateName: estateName,
          projectName: el.name,
          mapName: el.name,
          tagField: el.name,
          dataElType,
          label: estateName + ":" + el.name + ":project-" + idx,
          roleOptions: roleOptions,
          disabled: limitDisabled,
        });
      }
    });
  }

  if (teamProjects && Object.keys(teamProjects).length > 0) {
    Object.keys(teamProjects).forEach((teamMemberId) => {
      if (selectedPerson && selectedPerson.id === teamMemberId) return;
      const teamMemberProjects = teamProjects[teamMemberId];
      Object.values(teamMemberProjects).forEach((el, idx) => {
        if (!projectsIgnore.includes(el.id)) {
          projectsIgnore.push(el.id);
          // Get person role based on team member and all role options
          const estateId = el.estateId;
          const estateName = el.estateName;
          const limitDisabled = !!estateKeysNotAllowed?.includes(estateId);
          const currentRole = getCurrentDataRole({ userId: teamMemberId, el });
          const roleOptions = getRoleOptions({ allRoleOptions, el, userId: teamMemberId });
          const roleData = getRoleData({ el, userId: teamMemberId });
          if (selectedEstateId && selectedEstateId !== estateId) return;
          let dataElType = "project";
          if (el.projectStatus === "archived") dataElType = "archivedProject";
          tempProjList.push({
            estateId: estateId,
            projectId: el.id,
            estateName: estateName,
            projectName: el.name,
            mapName: el.name,
            tagField: el.name,
            dataElType,
            label: estateName + ":" + el.name + ":project-" + idx,
            roleOptions: roleOptions,
            role: currentRole,
            inviterId: teamMemberId,
            categories: roleData && roleData.categories ? roleData.categories : undefined,
            expireDate: roleData && roleData.expireDate ? roleData.expireDate : undefined,
            disabled: limitDisabled,
          });
        }
      });
    });
  }

  if (tempProjList.length > 0) {
    tempProjList = tempProjList
      .sort((a, b) => {
        const valueA = typeof a.label === "string" ? a.label : "";
        const valueB = typeof b.label === "string" ? b.label : "";
        return valueA.localeCompare(valueB);
      })
      .sort((a, b) =>
        sortAccordingToOrder(a.dataElType, b.dataElType, ["project", "archivedProject"]),
      );
  }
  // Collect list into one array
  const tempList: any[] = [...tempMapList, ...tempProjList].sort(
    sortAccordingToEstateIdAndDataElType,
  );
  const estateDisplayInput = tempList.map((el) => {
    const { orgId } = user;
    const estOrgId = orgEstates?.[el.estateId]?.orgId;
    const isExternal = !!orgEstates && orgId !== estOrgId;
    return {
      estateId: el.estateId,
      estateName: el.estateName,
      isExternal,
    };
  });
  // Inject estate overviews options
  const uniqueEstateNameMap = buildUniqueEstateNameMap(estateDisplayInput, externalText);
  const seenEstateIds = new Set<string>();
  let uniqueEstates: string[] = [];
  let estateIdsAndNames: { [key: string]: string } = {};

  tempList.forEach((el) => {
    const { orgId } = user;
    const estOrgId = orgEstates?.[el.estateId]?.orgId;
    const isExternal = !!orgEstates && orgId !== estOrgId;
    const displayEstateName = uniqueEstateNameMap[el.estateId] ?? el.estateName;

    if (!seenEstateIds.has(el.estateId) && !el.disableEstate) {
      seenEstateIds.add(el.estateId);

      let dataElType = "";
      const limitDisabled = !!estateKeysNotAllowed?.includes(el.estateId);
      let hideEstate = false;

      if (isExternal) {
        dataElType = "extEstate";
      }

      if (withUserEstateRole) {
        hideEstate = disableEstateUserRole({
          userOrg,
          estates,
          estateRole: userEstateRole[el.estateId],
          el,
        });
      }

      if (overviewText && overviewText !== "" && !hideEstate) {
        list.push({
          estateId: el.estateId,
          mapId: null,
          projectId: null,
          estateName: displayEstateName,
          mapName: overviewText,
          projectName: overviewText,
          tagField: displayEstateName,
          label: displayEstateName,
          dataElType: dataElType || "estate",
          roleOptions: el.roleOptions,
          disabled: limitDisabled,
        });
      }

      uniqueEstates.push(el.estateId);
      estateIdsAndNames[el.estateId] = displayEstateName;
    }

    list.push({
      ...el,
      estateName: displayEstateName,
      label:
        el.dataElType === "map"
          ? `${displayEstateName}:${el.mapName}:${el.dataElType}:${el.mapId}`
          : `${displayEstateName}:${el.projectName}:${el.dataElType}:${el.projectId}`,
    });
  });

  // If with show inventories add element to top of list
  if (withShowInventories) {
    list.unshift({
      estateId: "All Inventories",
      mapId: null,
      projectId: null,
      estateName: "All Estates",
      mapName: "All Inventories",
      projectName: "All Inventories",
      tagField: "All Inventories",
      label: "All Estates:All Inventories",
      dataElType: "allInventories",
    });
  }
  if (withSelectAllOption && uniqueEstates.length > 1) {
    list.unshift({
      estateId: "selectAllOptions",
      mapId: null,
      projectId: null,
      estateName: orgName ? orgName : selectAllText,
      mapName: selectAllText,
      projectName: selectAllText,
      tagField: "Select All",
      label: "selectAll:options",
      dataElType: "selectAllOptions",
    });
  }
  return { list, uniqueEstates, estateIdsAndNames };
};
export default getDataOptions;

// --- Helper functions --- //
interface GetRoleOptions {
  allRoleOptions: { id: RoleTypes; status: string; label: string }[];
  userId?: string;
  el: MapProps | ProjectProps;
}

function getRoleData({ el, userId }: { el: MapProps | ProjectProps; userId: string }) {
  if (!userId || !el.roles || !el.roles[userId]) return null;
  return el.roles[userId];
}

function getRoleOptions({ allRoleOptions, el, userId }: GetRoleOptions) {
  if (!userId || !el.roles || !el.roles[userId]) return [];
  return allRoleOptions.slice(
    0,
    allRoleOptions.findIndex((option) => option.id === el.roles![userId!].role) + 1,
  );
}

function getCurrentDataRole({ userId, el }: { userId?: string; el: MapProps | ProjectProps }) {
  if (!userId || !el.roles || !el.roles[userId]) return "viewer";
  return el.roles[userId].role;
}

function checkEstateAndOrgEmailMatch({
  userOrg,
  estates,
  el,
}: {
  userOrg?: UserOrg | null;
  estates?: { [key: string]: EstateProps } | null;
  el: MapProps | ProjectProps;
}) {
  // Input checking
  if (!userOrg || !estates || !el) return false;
  const orgEmail = userOrg?.email;
  const estateEmail = estates[el.estateId]?.email;
  const orgId = userOrg?.id;
  const estateOrgId = estates[el.estateId]?.orgId;
  if (orgId && estateOrgId && orgId === estateOrgId) return true;
  if (orgEmail && estateEmail && orgEmail === estateEmail) return true;

  return false;
}

function checkOrgToOrgConsumerSubscriptionMatch({
  orgToOrgConsumerSubscriptions,
  el,
}: {
  orgToOrgConsumerSubscriptions?: OrgToOrgSubscription[] | null;
  el: MapProps | ProjectProps;
}) {
  if (!orgToOrgConsumerSubscriptions || !el) return false;
  const orgToOrgConsumerSubscription = orgToOrgConsumerSubscriptions.find(
    (sub) => sub.estateId === el.estateId,
  );
  if (orgToOrgConsumerSubscription) return true;

  return false;
}

function disableEstateUserRole({
  userOrg,
  estates,
  estateRole,
  el,
}: {
  userOrg?: UserOrg | null;
  estates?: { [key: string]: EstateProps } | null;
  estateRole: RoleTypes;
  el: MapProps | ProjectProps;
}) {
  if (!userOrg || !estates || !el) return true;
  const orgId = userOrg?.id;
  const estateOrgId = estates[el.estateId]?.orgId;
  if (orgId && estateOrgId && orgId === estateOrgId) return false;
  if (estateRole === "admin") return false;

  return true;
}

// Sort the list according to estateId and dataElType
function sortAccordingToEstateIdAndDataElType(a: any, b: any) {
  // Sort by estateId first
  const valueA = typeof a.estateId === "string" ? a.estateId : "";
  const valueB = typeof b.estateId === "string" ? b.estateId : "";
  const estateIdComparison = valueA.localeCompare(valueB);
  // If estateId is the same, sort by dataElType
  if (estateIdComparison === 0) {
    if (a.dataElType === "map" && b.dataElType === "project") {
      return -1; // "map" comes before "project"
    } else if (a.dataElType === "project" && b.dataElType === "map") {
      return 1; // "project" comes after "map"
    }
  }

  return estateIdComparison;
}

// Build unique estate name map to handle duplicate estate names
function buildUniqueEstateNameMap(
  estates: Array<{ estateId: string; estateName: string; isExternal: boolean }>,
  externalText?: string
): Record<string, string> {
  const uniqueById = new Map<
    string,
    { estateId: string; estateName: string; isExternal: boolean }
  >();

  for (const estate of estates) {
    if (!uniqueById.has(estate.estateId)) {
      uniqueById.set(estate.estateId, estate);
    }
  }

  const groupedByName = new Map<
    string,
    Array<{ estateId: string; estateName: string; isExternal: boolean }>
  >();

  for (const estate of uniqueById.values()) {
    const arr = groupedByName.get(estate.estateName) ?? [];
    arr.push(estate);
    groupedByName.set(estate.estateName, arr);
  }

  const result: Record<string, string> = {};

  for (const [estateName, group] of groupedByName.entries()) {
    const internal = group
      .filter((e) => !e.isExternal)
      .sort((a, b) => a.estateId.localeCompare(b.estateId));

    const external = group
      .filter((e) => e.isExternal)
      .sort((a, b) => a.estateId.localeCompare(b.estateId));

    // ---- INTERNAL ----
    if (internal.length === 1) {
      result[internal[0].estateId] = estateName;
    } else if (internal.length > 1) {
      internal.forEach((estate, index) => {
        result[estate.estateId] = `${estateName} (${index + 1})`;
      });
    }

    // ---- EXTERNAL ----
    if (external.length === 1) {
      result[external[0].estateId] = `${estateName} (${externalText ?? "external"})`;
    } else if (external.length > 1) {
      external.forEach((estate, index) => {
        result[estate.estateId] = `${estateName} (${externalText ?? "external"} ${index + 1})`;
      });
    }
  }

  return result;
}
// function buildUniqueEstateNameMap(
//   items: Array<{ estateId: string; estateName: string }>,
// ): Record<string, string> {
//   const estateById = new Map<string, string>();

//   for (const item of items) {
//     if (!estateById.has(item.estateId)) {
//       estateById.set(item.estateId, item.estateName);
//     }
//   }

//   const groupedByName = new Map<string, Array<{ estateId: string; estateName: string }>>();

//   for (const [estateId, estateName] of estateById.entries()) {
//     const existing = groupedByName.get(estateName) ?? [];
//     existing.push({ estateId, estateName });
//     groupedByName.set(estateName, existing);
//   }

//   const result: Record<string, string> = {};

//   for (const [estateName, estatesWithSameName] of groupedByName.entries()) {
//     if (estatesWithSameName.length === 1) {
//       result[estatesWithSameName[0].estateId] = estateName;
//       continue;
//     }

//     // Stable ordering so names don't jump around between renders
//     estatesWithSameName
//       .sort((a, b) => a.estateId.localeCompare(b.estateId))
//       .forEach((estate, index) => {
//         result[estate.estateId] = `${estateName} (${index + 1})`;
//       });
//   }

//   return result;
// }
